import { hasActiveTrendConfig } from '../../pipeline/lib/summarizePrimaryPhaseStatPending';
import { isCompareAnalysisFiltersUsable } from '../../../filters/lib/statisticsCompareHelpers';
import { TREND_FIELD_KEYS, STATISTICS_BASE_FILTER_DEFAULTS, createStatisticsFilterDefaults, pickStatisticsScopeFields } from '../../../filters/lib/statisticsFilterParams';
import { isTrendDimensionAxisMode, TREND_AXIS_MODE, TREND_AXIS_MODE_OPTIONS } from '../../../filters/lib/statisticsTrendDimension';
import { resolveTrendMonthlySpecForQuery, TREND_MODE } from '../../../filters/lib/statisticsTrendFilters';
import { resolveComparePeriodChipLabel } from '../../../filters/lib/statisticsComparePresets';
import { rebaseLiveRollingPeriodFilters, resolveStatisticsPeriodDisplayLabel } from '../../../filters/lib/statisticsPeriodQuickPickUtils';
import { TREND_CHART_TYPE } from '../../trend/lib/trendChartConfig';
import { formatTrendYAxisMetricsLabel, normalizeTrendYAxisMetrics } from '../../trend/lib/trendYAxisMetrics';
import { hasCompositionChartData } from '../../composition/lib/compositionChartBuckets';
import {
	DEFAULT_COMPOSITION_CHART_CONFIG,
	formatCompositionChartMetricsLabel,
	getCompositionChartDimensionLabel,
	isCompositionChartMapType,
	normalizeCompositionChartConfig,
} from '../../composition/lib/compositionChartConfig';
import { DEFAULT_OVERDUE_CHART_CONFIG, buildOverdueChartTitle, hasOverdueChartData, normalizeOverdueChartConfig } from '../../overdue/lib/overdueStatChartData';
import { DASHBOARD_BUILTIN_OVERDUE_TEMPLATE_ID, DASHBOARD_OVERDUE_RANK_CHART_CONFIG, isBuiltinOverdueRankSeedSnapshot } from '../../overdue/lib/overdueChartConfig';
import { isHorizontalBarOrientation } from '../../shared/lib/barOrientation';
import { isMixedMetricSeriesTypes } from '../../shared/lib/metricSeriesTypes';
import { DASHBOARD_DRAG_APPEND_TARGET_ID } from '../../../../../utils/dashboardDragReorder.js';

export const STATISTICS_PERSISTED_CHARTS_MAX = null;
export const HOME_DASHBOARD_CHARTS_MAX = 4;
export const DASHBOARD_CHART_BOARD = {
	HOME: 'home',
	STATISTICS: 'statistics',
};
export const STATISTICS_PERSISTED_CHARTS_STORAGE_PREFIX = 'erp-statistics-saved-compare-charts';
export const HOME_DASHBOARD_CHARTS_STORAGE_PREFIX = 'erp-home-dashboard-charts';

/** 有限数量上限（首页等）；null/非有限 = 不限 */
export const isPersistedChartsCountLimited = (maxCharts) => Number.isFinite(maxCharts) && maxCharts > 0;

export const hasReachedPersistedChartsLimit = (count, maxCharts) =>
	isPersistedChartsCountLimited(maxCharts) && Number(count) >= maxCharts;

export const trimPersistedChartsToMax = (charts = [], maxCharts) => {
	if (!Array.isArray(charts)) return [];
	if (!isPersistedChartsCountLimited(maxCharts)) return charts;
	return charts.slice(0, maxCharts);
};

export const formatPersistedChartsCountLabel = (count, maxCharts) =>
	isPersistedChartsCountLimited(maxCharts) ? `${count} / ${maxCharts}` : String(count);
/** 统计页经营看板卡片预览区基准高度（1 行；加高时由 resolveStatisticsDashboardChartPreviewHeight 计算） */
export const STATISTICS_DASHBOARD_CHART_PREVIEW_HEIGHT = '22rem';

/** @deprecated 使用 STATISTICS_DASHBOARD_CHART_PREVIEW_HEIGHT */
export const PERSISTED_CHART_DEFAULT_PREVIEW_HEIGHT = STATISTICS_DASHBOARD_CHART_PREVIEW_HEIGHT;

export const PERSISTED_CHART_ROW_SPAN_MIN = 1;
export const PERSISTED_CHART_ROW_SPAN_MAX = 2;

export const normalizePersistedChartRowSpan = (value) => {
	const span = Math.round(Number(value));
	if (!Number.isFinite(span)) return PERSISTED_CHART_ROW_SPAN_MIN;
	return Math.min(PERSISTED_CHART_ROW_SPAN_MAX, Math.max(PERSISTED_CHART_ROW_SPAN_MIN, span));
};

export const resolvePersistedChartRowSpan = (snapshot = {}, charts = null) => {
	const chartList = Array.isArray(charts) ? charts : null;
	const builtinUi = resolveBuiltinDashboardChartUi(snapshot, PERSISTED_CHART_COL_SPAN_MAX, chartList ?? []);
	if (builtinUi) return builtinUi.rowSpan;

	const saved = snapshot?.ui?.rowSpan;
	if (saved >= PERSISTED_CHART_ROW_SPAN_MIN && saved <= PERSISTED_CHART_ROW_SPAN_MAX) {
		return normalizePersistedChartRowSpan(saved);
	}
	return PERSISTED_CHART_ROW_SPAN_MIN;
};

export const getPersistedChartRowSpanLabel = (rowSpan = 1) => (normalizePersistedChartRowSpan(rowSpan) >= 2 ? '加高' : '标准');

export const getPersistedChartRowSpanCycle = (currentSpan = 1) => {
	const current = normalizePersistedChartRowSpan(currentSpan);
	return current >= PERSISTED_CHART_ROW_SPAN_MAX ? PERSISTED_CHART_ROW_SPAN_MIN : current + 1;
};

export const resolveStatisticsDashboardChartPreviewHeight = (rowSpan = 1) => {
	if (normalizePersistedChartRowSpan(rowSpan) >= PERSISTED_CHART_ROW_SPAN_MAX) {
		return null;
	}
	return STATISTICS_DASHBOARD_CHART_PREVIEW_HEIGHT;
};

/** 统计页经营看板卡片预览高度（随 ui.rowSpan 变化；加高时返回 null 表示填满栅格行） */
export const resolvePersistedChartPreviewHeight = (snapshot = {}) => resolveStatisticsDashboardChartPreviewHeight(resolvePersistedChartRowSpan(snapshot));

/** 锁定经营看板预览槽高度，骨架屏与加载完成后保持一致 */
export const buildStatisticsDashboardChartPreviewSlotStyle = (height = STATISTICS_DASHBOARD_CHART_PREVIEW_HEIGHT) => ({
	'--statistics-saved-chart-preview-height': height,
	flex: `0 0 ${height}`,
	height,
	minHeight: height,
	maxHeight: height,
	overflow: 'hidden',
	boxSizing: 'border-box',
});

/** 加高卡片：预览区随 grid-row span 拉伸 */
export const buildStatisticsDashboardChartTallPreviewSlotStyle = () => ({
	flex: '1 1 auto',
	minHeight: 0,
	height: 'auto',
	maxHeight: 'none',
	overflow: 'hidden',
	boxSizing: 'border-box',
});

export const PERSISTED_CHART_COL_SPAN_MIN = 1;
export const PERSISTED_CHART_COL_SPAN_MAX = 4;
export const PERSISTED_CHART_GRID_COLUMNS_DESKTOP = 4;

/** 与 backend/utils/statistics/dashboardChartTemplates.js 保持一致 */
export const DASHBOARD_BUILTIN_CHART_LAYOUT = {
	'preset-bar': { colSpan: 1, rowSpan: 1 },
	'preset-pie': { colSpan: 1, rowSpan: 1 },
	'preset-dimension': { colSpan: 1, rowSpan: 1 },
	'preset-map': { colSpan: 1, rowSpan: 1 },
	'preset-overdue': { colSpan: 1, rowSpan: 2 },
};

/** 与 backend dashboardChartTemplates 一致：3 列固定槽位（由 SCSS 应用） */
export const DASHBOARD_BUILTIN_DEFAULT_GRID_PLACEMENT = {
	'preset-bar': { gridColumn: 1, gridRow: 1 },
	'preset-pie': { gridColumn: 2, gridRow: 1 },
	'preset-dimension': { gridColumn: 1, gridRow: 2 },
	'preset-map': { gridColumn: 2, gridRow: 2 },
	'preset-overdue': { gridColumn: 3, gridRow: '1 / span 2' },
};

export const BUILTIN_DASHBOARD_TEMPLATE_IDS = Object.keys(DASHBOARD_BUILTIN_CHART_LAYOUT);

export const isStatisticsBuiltinDashboardChartSet = (charts = []) => {
	if (charts.length !== 5) return false;
	const templateIds = new Set(BUILTIN_DASHBOARD_TEMPLATE_IDS);
	return charts.every((chart) => chart?.source === 'builtin' && templateIds.has(chart.templateId ?? chart.id));
};

/** 5 张 builtin 且未手动调宽/高：启用固定 3 列排版 */
export const isStatisticsDefaultBuiltinDashboardLayout = (charts = []) =>
	isStatisticsBuiltinDashboardChartSet(charts) && !charts.some((chart) => chart?.ui?.colSpanTouched || chart?.ui?.rowSpanTouched);

export const chartUsesDefaultBuiltinGridPlacement = (snapshot = {}, charts = []) => {
	if (snapshot?.source !== 'builtin') return false;
	if (snapshot?.ui?.useDefaultGridPlacement === true && !snapshot?.ui?.colSpanTouched && !snapshot?.ui?.rowSpanTouched) {
		return true;
	}
	return isStatisticsDefaultBuiltinDashboardLayout(charts);
};

export const clearPersistedChartDefaultGridPlacement = (chart = {}) => {
	if (!chart?.ui?.useDefaultGridPlacement) return chart;
	const nextUi = { ...(chart.ui ?? {}) };
	delete nextUi.useDefaultGridPlacement;
	return { ...chart, ui: nextUi };
};

const resolveBuiltinDashboardChartLayout = (snapshot = {}) => {
	const key = snapshot?.templateId ?? snapshot?.id;
	return DASHBOARD_BUILTIN_CHART_LAYOUT[key] ?? null;
};

/** builtin 在未手动调宽/高前，使用模板默认 col/row 布局 */
export const resolveBuiltinDashboardChartUi = (snapshot = {}, maxColumns = PERSISTED_CHART_COL_SPAN_MAX, charts = []) => {
	const layout = resolveBuiltinDashboardChartLayout(snapshot);
	if (!layout || snapshot?.source !== 'builtin') return null;
	const usePinnedGrid = chartUsesDefaultBuiltinGridPlacement(snapshot, charts);
	return {
		colSpan: usePinnedGrid
			? normalizePersistedChartColSpan(layout.colSpan, maxColumns)
			: snapshot?.ui?.colSpanTouched
				? normalizePersistedChartColSpan(snapshot?.ui?.colSpan ?? layout.colSpan, maxColumns)
				: normalizePersistedChartColSpan(layout.colSpan, maxColumns),
		rowSpan: usePinnedGrid
			? normalizePersistedChartRowSpan(layout.rowSpan)
			: snapshot?.ui?.rowSpanTouched
				? normalizePersistedChartRowSpan(snapshot?.ui?.rowSpan ?? layout.rowSpan)
				: normalizePersistedChartRowSpan(layout.rowSpan),
	};
};

/** 仪表板瀑布砖默认布局（未手动调宽时使用） */
export const MASONRY_TILE_PATTERNS = {
	1: ['featured'],
	2: ['wide', 'compact'],
	3: ['wide', 'compact', 'tall'],
	4: ['compact', 'compact', 'compact', 'compact'],
	5: ['wide', 'compact', 'compact', 'compact', 'tall'],
	6: ['wide', 'compact', 'compact', 'tall', 'compact', 'tall'],
	7: ['wide', 'compact', 'compact', 'tall', 'compact', 'compact', 'tall'],
	8: ['wide', 'compact', 'compact', 'tall', 'wide', 'compact', 'compact', 'tall'],
	9: ['wide', 'compact', 'compact', 'tall', 'compact', 'wide', 'compact', 'compact', 'tall'],
};

const AUTO_TILE_COL_SPAN = {
	featured: 1,
	compact: 1,
	tall: 1,
	wide: 2,
	hero: 2,
};

export const resolveAutoMasonryTile = (index, total) => {
	const patternMax = Math.max(...Object.keys(MASONRY_TILE_PATTERNS).map((key) => Number(key)).filter(Number.isFinite), 1);
	const count = Math.min(Math.max(total, 1), patternMax);
	return MASONRY_TILE_PATTERNS[count]?.[index] ?? 'compact';
};

export const normalizePersistedChartColSpan = (value, maxColumns = PERSISTED_CHART_COL_SPAN_MAX) => {
	const max = Math.min(PERSISTED_CHART_COL_SPAN_MAX, Math.max(PERSISTED_CHART_COL_SPAN_MIN, maxColumns));
	const span = Math.round(Number(value));
	if (!Number.isFinite(span)) return PERSISTED_CHART_COL_SPAN_MIN;
	return Math.min(max, Math.max(PERSISTED_CHART_COL_SPAN_MIN, span));
};

export const resolvePersistedChartColSpan = (snapshot = {}, index = 0, total = 1, maxColumns = PERSISTED_CHART_COL_SPAN_MAX, charts = null) => {
	const chartList = Array.isArray(charts) ? charts : null;
	const builtinUi = resolveBuiltinDashboardChartUi(snapshot, maxColumns, chartList ?? []);
	if (builtinUi) return builtinUi.colSpan;

	const saved = snapshot?.ui?.colSpan;
	if (saved >= PERSISTED_CHART_COL_SPAN_MIN && saved <= PERSISTED_CHART_COL_SPAN_MAX) {
		return normalizePersistedChartColSpan(saved, maxColumns);
	}
	const autoTile = resolveAutoMasonryTile(index, total);
	return normalizePersistedChartColSpan(AUTO_TILE_COL_SPAN[autoTile] ?? 1, maxColumns);
};

/** 首页经营看板：未手动调宽时默认占满当前行（4 列栅格下为全宽） */
export const resolveDefaultHomeDashboardChartColSpan = (chartCount = 1, maxColumns = PERSISTED_CHART_COL_SPAN_MAX) => {
	const total = Math.max(1, Math.round(Number(chartCount)) || 1);
	if (total <= 1) {
		return normalizePersistedChartColSpan(maxColumns, maxColumns);
	}
	return normalizePersistedChartColSpan(Math.max(1, Math.floor(maxColumns / total)), maxColumns);
};

export const resolveHomeDashboardChartColSpan = (snapshot = {}, index = 0, total = 1, maxColumns = PERSISTED_CHART_COL_SPAN_MAX) => {
	const saved = snapshot?.ui?.colSpan;
	if (saved >= PERSISTED_CHART_COL_SPAN_MIN && saved <= PERSISTED_CHART_COL_SPAN_MAX) {
		return normalizePersistedChartColSpan(saved, maxColumns);
	}
	return resolveDefaultHomeDashboardChartColSpan(total, maxColumns);
};

export const getPersistedChartColSpanLabel = (colSpan = 1, maxColumns = PERSISTED_CHART_GRID_COLUMNS_DESKTOP) => {
	const span = normalizePersistedChartColSpan(colSpan, maxColumns);
	const max = normalizePersistedChartColSpan(maxColumns, maxColumns);
	if (span >= max) return '全宽';
	if (max === 4) {
		if (span >= 3) return '宽';
		if (span >= 2) return '半宽';
		return '窄';
	}
	if (span >= 2) return '宽';
	return '窄';
};

export const getPersistedChartColSpanCycle = (currentSpan = 1, maxColumns = PERSISTED_CHART_COL_SPAN_MAX) => {
	const max = normalizePersistedChartColSpan(maxColumns, maxColumns);
	const choices = max <= 1 ? [1] : max === 2 ? [1, 2] : max === 3 ? [1, 2, 3] : [1, 2, 3, 4];
	const current = normalizePersistedChartColSpan(currentSpan, max);
	const index = choices.indexOf(current);
	const nextIndex = index < 0 ? 0 : (index + 1) % choices.length;
	return choices[nextIndex];
};

/** 首页经营看板：优先逐级缩窄，最窄后再回到全宽 */
export const getHomeDashboardChartColSpanCycle = (currentSpan = 1, maxColumns = PERSISTED_CHART_COL_SPAN_MAX) => {
	const max = normalizePersistedChartColSpan(maxColumns, maxColumns);
	const current = normalizePersistedChartColSpan(currentSpan, max);
	if (max <= 1) return 1;
	if (current > 1) return current - 1;
	return max;
};

const savedChartWidthForColSpan = (colSpan, colWidth, gap) => colSpan * colWidth + Math.max(0, colSpan - 1) * gap;

/** 拖拽开始时锁定卡片左缘与可扩列上限，避免预览改宽时 grid 重排导致指针换算漂移 */
export const resolvePersistedChartResizeConstraints = ({ itemEl, gridEl, maxColumns = PERSISTED_CHART_COL_SPAN_MAX }) => {
	const max = normalizePersistedChartColSpan(maxColumns, maxColumns);
	if (max <= 1 || !gridEl || !itemEl) {
		return { anchorLeft: 0, maxAllowedSpan: 1, colWidth: 0, gap: 0 };
	}

	const gridRect = gridEl.getBoundingClientRect();
	const itemRect = itemEl.getBoundingClientRect();
	const style = getComputedStyle(gridEl);
	const gap = Number.parseFloat(style.columnGap || style.gap) || 0;
	const colWidth = (gridRect.width - gap * (max - 1)) / max;
	const step = colWidth + gap;
	const itemOffsetX = itemRect.left - gridRect.left;
	const startCol = Math.min(max, Math.max(1, Math.floor((itemOffsetX + gap / 2) / step) + 1));
	const maxAllowedSpan = Math.max(1, max - startCol + 1);

	return {
		anchorLeft: itemRect.left,
		maxAllowedSpan,
		colWidth,
		gap,
	};
};

/** 按卡片左缘到指针的水平距离换算列宽（相对拖拽，非 grid 绝对坐标） */
export const computeColSpanFromResizePointer = ({ clientX, anchorLeft, maxAllowedSpan, colWidth, gap, maxColumns = PERSISTED_CHART_COL_SPAN_MAX }) => {
	const max = normalizePersistedChartColSpan(maxColumns, maxColumns);
	const cap = Math.min(max, Math.max(1, maxAllowedSpan ?? max));
	if (cap <= 1 || colWidth <= 0 || !Number.isFinite(clientX) || !Number.isFinite(anchorLeft)) {
		return 1;
	}

	const spanWidth = Math.max(0, clientX - anchorLeft);
	const widthForSpan = (colSpan) => savedChartWidthForColSpan(colSpan, colWidth, gap);

	for (let colSpan = cap; colSpan >= 1; colSpan -= 1) {
		const minWidth = colSpan === 1 ? 0 : (widthForSpan(colSpan - 1) + widthForSpan(colSpan)) / 2;
		if (spanWidth >= minWidth) {
			return normalizePersistedChartColSpan(colSpan, maxColumns);
		}
	}

	return 1;
};

export const PERSISTED_CHART_KIND = {
	TREND: 'trend',
	COMPOSITION: 'composition',
	OVERDUE: 'overdue',
	PIPELINE_BAR: 'pipeline-bar',
	PIPELINE_PIE: 'pipeline-pie',
	PIPELINE_DIMENSION: 'pipeline-dimension',
	PIPELINE_MAP: 'pipeline-map',
};

/** builtin 经营看板图（流程分布/占比/贡献排行/客户分布），非用户自制报表 */
export const isPipelineBuiltinChartKind = (chartKind) => typeof chartKind === 'string' && chartKind.startsWith('pipeline-');

const LEGACY_ORDER_COMPLETION_KIND = 'orderCompletion';
const LEGACY_TREND_CHART_KIND = 'lineCompare';
const LEGACY_COMPOSITION_CHART_KIND = 'custom';

const LEGACY_TREND_FILTER_KEYS = {
	lineCompareAnchor: 'trendAnchor',
	lineCompareMode: 'trendMode',
	lineCompareMonthlyYears: 'trendMonthlyYears',
	lineCompareMonthlyYoy: 'trendMonthlyYoy',
	lineCompareAxisMode: 'trendAxisMode',
	lineCompareYAxisMetric: 'trendYAxisMetric',
	lineCompareYAxisMetrics: 'trendYAxisMetrics',
	lineCompareChartType: 'trendChartType',
	lineCompareLineShape: 'trendLineShape',
	lineCompareBarOrientation: 'trendBarOrientation',
	lineCompareMetricSeriesTypes: 'trendMetricSeriesTypes',
};

export const normalizeTrendFilterKeys = (filters = {}) => {
	const next = { ...filters };
	for (const [legacyKey, modernKey] of Object.entries(LEGACY_TREND_FILTER_KEYS)) {
		if (legacyKey in next && next[modernKey] === undefined) {
			next[modernKey] = next[legacyKey];
		}
		delete next[legacyKey];
	}
	return next;
};

export const resolvePersistedChartKind = (snapshot = {}) => {
	const kind = snapshot?.chartKind ?? PERSISTED_CHART_KIND.TREND;
	if (kind === LEGACY_ORDER_COMPLETION_KIND || kind === LEGACY_COMPOSITION_CHART_KIND) return PERSISTED_CHART_KIND.COMPOSITION;
	if (kind === LEGACY_TREND_CHART_KIND) return PERSISTED_CHART_KIND.TREND;
	return kind;
};

/** 固定报表拉数是否需 includeOrders（时间轴折线仅需 compares/lenses） */
export const resolvePersistedChartIncludeOrders = (snapshot = {}) => {
	const kind = resolvePersistedChartKind(snapshot);
	if (kind === PERSISTED_CHART_KIND.OVERDUE) return false;
	/** 组成/流程地图预览走 chartAggregates.geo；全屏钻取再由 includeOrdersChartIds 打开明细 */
	if (kind === PERSISTED_CHART_KIND.COMPOSITION) return false;
	if (kind === PERSISTED_CHART_KIND.PIPELINE_MAP) return false;
	if (kind === PERSISTED_CHART_KIND.PIPELINE_BAR || kind === PERSISTED_CHART_KIND.PIPELINE_PIE || kind === PERSISTED_CHART_KIND.PIPELINE_DIMENSION) {
		return false;
	}

	const filters = buildPersistedChartFilters(snapshot);
	if ((filters.trendChartType ?? 'line') === TREND_CHART_TYPE.MAP) return true;
	// 全年各月/各季预览只靠 trendPeriodYoy；拉多年份全量订单易超时导致图空白。全屏钻取由 batch includeOrdersChartIds 单独打开。
	if (filters.trendMode === TREND_MODE.YEAR_MONTHS) return false;
	const axisMode = filters.trendAxisMode ?? TREND_AXIS_MODE.TIME;
	if (axisMode === TREND_AXIS_MODE.TIME || !axisMode) return false;
	return isTrendDimensionAxisMode(axisMode);
};

/** 固定报表是否为地图类（折线对比地图 / 自定义地图） */
export const resolvePersistedChartIsMapPreview = (snapshot = {}, chartBundle = null) => {
	const chartKind = resolvePersistedChartKind(snapshot);
	if (chartKind === PERSISTED_CHART_KIND.TREND) {
		const filters = buildPersistedChartFilters(snapshot);
		const chartType = chartBundle?.trendChartType ?? filters.trendChartType;
		return chartType === TREND_CHART_TYPE.MAP;
	}
	if (chartKind === PERSISTED_CHART_KIND.COMPOSITION) {
		const config = chartBundle?.chartConfig ?? snapshot?.chartConfig;
		return isCompositionChartMapType(normalizeCompositionChartConfig(config).chartType);
	}
	return false;
};

export const resolveOverdueChartConfigFromSnapshot = (snapshot = {}) => {
	if (resolvePersistedChartKind(snapshot) !== PERSISTED_CHART_KIND.OVERDUE) {
		return normalizeOverdueChartConfig(DEFAULT_OVERDUE_CHART_CONFIG);
	}
	if (!snapshot?.overdueConfig && isBuiltinOverdueRankSeedSnapshot(snapshot)) {
		return normalizeOverdueChartConfig(DASHBOARD_OVERDUE_RANK_CHART_CONFIG);
	}
	return normalizeOverdueChartConfig(snapshot?.overdueConfig ?? DEFAULT_OVERDUE_CHART_CONFIG);
};

export const resolveCompositionChartConfigFromSnapshot = (snapshot = {}) => {
	if (resolvePersistedChartKind(snapshot) !== PERSISTED_CHART_KIND.COMPOSITION) {
		return normalizeCompositionChartConfig(DEFAULT_COMPOSITION_CHART_CONFIG);
	}
	if (snapshot?.chartKind === LEGACY_ORDER_COMPLETION_KIND && !snapshot?.chartConfig) {
		return normalizeCompositionChartConfig({
			dimension: 'isCompleted',
			metric: 'count',
			chartType: 'donut',
		});
	}
	return normalizeCompositionChartConfig(snapshot?.chartConfig ?? DEFAULT_COMPOSITION_CHART_CONFIG);
};

export const getStatisticsPersistedChartsStorageKey = (userId, prefix = STATISTICS_PERSISTED_CHARTS_STORAGE_PREFIX) => `${prefix}:${userId || 'anonymous'}`;

export const pickTrendFields = (filters = {}) => {
	const result = {};
	for (const key of TREND_FIELD_KEYS) {
		result[key] = filters[key] ?? STATISTICS_BASE_FILTER_DEFAULTS[key];
	}
	return result;
};

export const buildPersistedChartFilters = (snapshot = {}) =>
	rebaseLiveRollingPeriodFilters(
		{
			...createStatisticsFilterDefaults(),
			...(snapshot?.scope ?? {}),
			...normalizeTrendFilterKeys(snapshot?.trend ?? snapshot?.lineCompare ?? {}),
		},
		snapshot,
	);

const PERSISTED_CHART_TYPE_LABELS = {
	line: '折线',
	bar: '柱状',
	pie: '饼图',
	donut: '环形',
	funnel: '漏斗',
	map: '地图',
};

const resolveChartTypeDisplayLabel = (chartType, barOrientation, fallback = '柱状') => {
	if (chartType === 'bar' && isHorizontalBarOrientation(barOrientation)) return '条形';
	return PERSISTED_CHART_TYPE_LABELS[chartType] ?? fallback;
};

const toPersistedChartPreviewKind = (chartType, barOrientation, seriesTypes, fallback = 'bar') => {
	if (isMixedMetricSeriesTypes(seriesTypes)) return 'combo';
	if (chartType === 'bar' && isHorizontalBarOrientation(barOrientation)) return 'bar-h';
	if (chartType && PERSISTED_CHART_TYPE_LABELS[chartType]) return chartType;
	return fallback;
};

/** 图种展示名：经营图用图表类型，超期与流程保持原称。 */
export const resolvePersistedChartTypeLabel = (snapshot = {}) => {
	const kind = resolvePersistedChartKind(snapshot);
	if (kind === PERSISTED_CHART_KIND.OVERDUE) return '超期';
	if (isPipelineBuiltinChartKind(kind)) return '流程';
	if (kind === PERSISTED_CHART_KIND.COMPOSITION) {
		const chartConfig = resolveCompositionChartConfigFromSnapshot(snapshot);
		return resolveChartTypeDisplayLabel(chartConfig.chartType, chartConfig.barOrientation);
	}
	const filters = buildPersistedChartFilters(snapshot);
	return resolveChartTypeDisplayLabel(filters.trendChartType, filters.trendBarOrientation, '折线');
};

/** 图表库卡片封面缩略图类型（与图种一致） */
export const resolvePersistedChartPreviewKind = (snapshot = {}) => {
	const kind = resolvePersistedChartKind(snapshot);
	if (kind === PERSISTED_CHART_KIND.PIPELINE_BAR) return 'bar';
	if (kind === PERSISTED_CHART_KIND.PIPELINE_PIE) return 'donut';
	if (kind === PERSISTED_CHART_KIND.PIPELINE_DIMENSION) return 'bar-h';
	if (kind === PERSISTED_CHART_KIND.PIPELINE_MAP) return 'map';
	if (kind === PERSISTED_CHART_KIND.OVERDUE) {
		const overdue = resolveOverdueChartConfigFromSnapshot(snapshot);
		return toPersistedChartPreviewKind(overdue.chartType, overdue.barOrientation, undefined, 'bar-h');
	}
	if (kind === PERSISTED_CHART_KIND.COMPOSITION) {
		const chartConfig = resolveCompositionChartConfigFromSnapshot(snapshot);
		return toPersistedChartPreviewKind(
			chartConfig.chartType,
			chartConfig.barOrientation,
			chartConfig.metricSeriesTypes,
		);
	}
	const filters = buildPersistedChartFilters(snapshot);
	return toPersistedChartPreviewKind(
		filters.trendChartType,
		filters.trendBarOrientation,
		filters.trendMetricSeriesTypes,
		'line',
	);
};

/**
 * 趋势图是否可固定。
 * @param {object} filters
 * @param {{ allowWithoutCompare?: boolean }} [options] 制作区预览可先出本期图时，保存与预览对齐，不强制同比/对比段
 */
export const canSaveTrendChartSnapshot = (filters = {}, { allowWithoutCompare = false } = {}) => {
	const axisMode = filters.trendAxisMode ?? TREND_AXIS_MODE.TIME;
	if (isTrendDimensionAxisMode(axisMode)) return true;
	if (allowWithoutCompare) return true;
	if (!isCompareAnalysisFiltersUsable(filters)) return false;
	return hasActiveTrendConfig(filters);
};

export const createPersistedChartSnapshotId = () =>
	typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : `saved-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const createSnapshotId = createPersistedChartSnapshotId;

export const buildCompositionChartTitle = (chartConfig = {}, filters = {}) => {
	const config = normalizeCompositionChartConfig(chartConfig);
	const period = resolveStatisticsPeriodDisplayLabel(filters.startDate, filters.endDate);
	const parts = [getCompositionChartDimensionLabel(config.dimension), formatCompositionChartMetricsLabel(config.metrics, '&'), period].filter(Boolean);
	return parts.join(' · ') || '组成统计';
};

export const buildOverdueChartSnapshot = ({ title = '', overdueConfig = DEFAULT_OVERDUE_CHART_CONFIG }) => {
	const normalizedConfig = normalizeOverdueChartConfig(overdueConfig);
	return {
		id: createSnapshotId(),
		chartKind: PERSISTED_CHART_KIND.OVERDUE,
		title: title?.trim() || buildOverdueChartTitle(normalizedConfig),
		createdAt: new Date().toISOString(),
		selectedPhase: null,
		scope: {},
		trend: {},
		overdueConfig: normalizedConfig,
		ui: {},
	};
};

export const buildCompositionChartSnapshot = ({
	filters = {},
	selectedPhase = null,
	title = '',
	chartConfig = DEFAULT_COMPOSITION_CHART_CONFIG,
}) => {
	const normalizedConfig = normalizeCompositionChartConfig(chartConfig);
	return {
		id: createSnapshotId(),
		chartKind: PERSISTED_CHART_KIND.COMPOSITION,
		title: title?.trim() || buildCompositionChartTitle(normalizedConfig, filters),
		createdAt: new Date().toISOString(),
		selectedPhase,
		scope: pickStatisticsScopeFields(filters),
		trend: pickTrendFields(filters),
		chartConfig: normalizedConfig,
		ui: {},
	};
};

export const canSaveChartSnapshot = ({
	chartKind = PERSISTED_CHART_KIND.TREND,
	filters = {},
	orders = [],
	primaryPhaseStats = null,
	chartConfig = DEFAULT_COMPOSITION_CHART_CONFIG,
	overdueData = {},
	overdueConfig = DEFAULT_OVERDUE_CHART_CONFIG,
	selectedPhase = null,
} = {}) => {
	const resolvedKind = resolvePersistedChartKind({ chartKind });
	if (resolvedKind === PERSISTED_CHART_KIND.OVERDUE) {
		return hasOverdueChartData(overdueData, overdueConfig);
	}
	if (resolvedKind === PERSISTED_CHART_KIND.COMPOSITION) {
		return hasCompositionChartData(orders, chartConfig, { selectedPhase, primaryPhaseStats });
	}
	return canSaveTrendChartSnapshot(filters);
};

const clonePersistedChartSnapshot = (snapshot = {}) =>
	JSON.parse(
		JSON.stringify({
			...snapshot,
			chartKind: resolvePersistedChartKind(snapshot),
			chartConfig: resolveCompositionChartConfigFromSnapshot(snapshot),
			overdueConfig: resolveOverdueChartConfigFromSnapshot(snapshot),
			scope: snapshot.scope ?? {},
			trend: snapshot.trend ?? {},
			ui: snapshot.ui ?? { showPointLabels: false },
		}),
	);

export const buildDuplicatePersistedChartTitle = (title = '') => {
	const base = title?.trim() || '对比分析';
	const suffix = ' · 副本';
	if (base.endsWith(suffix)) return `${base} 2`.slice(0, 96);
	if (base.endsWith(' 2') && base.includes('副本')) return `${base.slice(0, -2)} 3`.slice(0, 96);
	return `${base}${suffix}`.slice(0, 96);
};

export const buildFiltersFromPersistedSnapshot = (snapshot = {}) => buildPersistedChartFilters(snapshot);

/** 从已固定报表快照还原首页看板保存参数（live 口径，不含 dataSnapshot） */
export const buildHomeChartSaveParamsFromSnapshot = (snapshot = {}) => {
	const chartKind = resolvePersistedChartKind(snapshot);
	const isBuiltinOverdueRank = chartKind === PERSISTED_CHART_KIND.OVERDUE && isBuiltinOverdueRankSeedSnapshot(snapshot);
	return {
		filters: buildFiltersFromPersistedSnapshot(snapshot),
		selectedPhase: snapshot.selectedPhase ?? null,
		title: snapshot.title,
		chartKind,
		chartConfig: chartKind === PERSISTED_CHART_KIND.COMPOSITION ? resolveCompositionChartConfigFromSnapshot(snapshot) : undefined,
		overdueConfig:
			chartKind === PERSISTED_CHART_KIND.OVERDUE ? (isBuiltinOverdueRank ? DASHBOARD_OVERDUE_RANK_CHART_CONFIG : resolveOverdueChartConfigFromSnapshot(snapshot)) : undefined,
		sourceStatisticsChartId: snapshot.id,
		liveDisplayFallback: snapshot.dataSnapshot && typeof snapshot.dataSnapshot === 'object' ? snapshot.dataSnapshot : undefined,
		...(isBuiltinOverdueRank
			? {
					pinMeta: {
						source: 'builtin',
						templateId: DASHBOARD_BUILTIN_OVERDUE_TEMPLATE_ID,
					},
				}
			: {}),
	};
};

/** 经营看板图表口径的稳定键，用于首页去重（不含 id / 标题 / UI） */
export const buildPersistedChartDefinitionKey = (snapshot = {}) => {
	const chartKind = resolvePersistedChartKind(snapshot);
	return JSON.stringify({
		chartKind,
		selectedPhase: snapshot.selectedPhase ?? null,
		scope: snapshot.scope ?? {},
		trend: snapshot.trend ?? {},
		chartConfig: chartKind === PERSISTED_CHART_KIND.COMPOSITION ? resolveCompositionChartConfigFromSnapshot(snapshot) : undefined,
		overdueConfig: chartKind === PERSISTED_CHART_KIND.OVERDUE ? resolveOverdueChartConfigFromSnapshot(snapshot) : undefined,
	});
};

export const findHomeChartMatchingStatisticsSnapshot = (statisticsSnapshot = {}, homeCharts = []) => {
	const sourceId = statisticsSnapshot?.id;
	const self = sourceId ? homeCharts.find((item) => item?.id === sourceId) : null;
	if (self) return self;
	if (sourceId) {
		const bySource = homeCharts.find((item) => item.sourceStatisticsChartId === sourceId);
		if (bySource) return bySource;
	}
	const definitionKey = buildPersistedChartDefinitionKey(statisticsSnapshot);
	return (
		homeCharts.find((item) => {
			if (item?.sourceStatisticsChartId && item.sourceStatisticsChartId !== sourceId) return false;
			return buildPersistedChartDefinitionKey(item) === definitionKey;
		}) ?? null
	);
};

/**
 * 图表库里哪些图算「已在首页」。
 * 先认 sourceStatisticsChartId；没有来源时，每张首页图最多再认一张口径相同的库图，避免相似图被重复计数。
 */
export const collectPinnedStatisticsChartIds = (statisticsCharts = [], homeCharts = []) => {
	const pinned = new Set();
	for (const homeChart of homeCharts) {
		const sourceId = homeChart?.sourceStatisticsChartId;
		if (!sourceId) continue;
		if (statisticsCharts.some((chart) => chart?.id === sourceId)) pinned.add(sourceId);
	}
	for (const homeChart of homeCharts) {
		if (homeChart?.sourceStatisticsChartId) continue;
		const definitionKey = buildPersistedChartDefinitionKey(homeChart);
		const match = statisticsCharts.find((chart) => !pinned.has(chart?.id) && buildPersistedChartDefinitionKey(chart) === definitionKey);
		if (match?.id) pinned.add(match.id);
	}
	return pinned;
};

/** 首页上有、图表库里已经对不上的图（来源被删，或没有可独占的同口径库图）。 */
export const listHomeChartsAbsentFromLibrary = (statisticsCharts = [], homeCharts = []) => {
	const libraryIds = new Set((statisticsCharts ?? []).map((chart) => chart?.id).filter(Boolean));
	const pinnedIds = collectPinnedStatisticsChartIds(statisticsCharts, homeCharts);
	return (homeCharts ?? []).filter((homeChart) => {
		if (!homeChart?.id || libraryIds.has(homeChart.id)) return false;
		const sourceId = homeChart.sourceStatisticsChartId;
		if (sourceId && libraryIds.has(sourceId)) return false;
		if (!sourceId) {
			const definitionKey = buildPersistedChartDefinitionKey(homeChart);
			const claimed = (statisticsCharts ?? []).some(
				(chart) => pinnedIds.has(chart?.id) && buildPersistedChartDefinitionKey(chart) === definitionKey,
			);
			if (claimed) return false;
		}
		return true;
	});
};

const buildChartUpdateParamsFromSnapshot = (chartId, snapshot = {}) => {
	const chartKind = resolvePersistedChartKind(snapshot);
	return {
		chartId,
		filters: buildFiltersFromPersistedSnapshot(snapshot),
		selectedPhase: snapshot.selectedPhase ?? null,
		title: snapshot.title,
		chartKind,
		chartConfig: chartKind === PERSISTED_CHART_KIND.COMPOSITION ? resolveCompositionChartConfigFromSnapshot(snapshot) : undefined,
		overdueConfig: chartKind === PERSISTED_CHART_KIND.OVERDUE ? resolveOverdueChartConfigFromSnapshot(snapshot) : undefined,
	};
};

/** 首页副本与图表库源图的口径、标题是否已一致（布局 ui 不参与） */
export const homeChartDefinitionMatchesStatisticsSnapshot = (homeChart = {}, statisticsSnapshot = {}) => {
	if ((homeChart.title ?? '') !== (statisticsSnapshot.title ?? '')) return false;
	return buildPersistedChartDefinitionKey(homeChart) === buildPersistedChartDefinitionKey(statisticsSnapshot);
};

/** 图表库保存后，写回对应首页副本（保持首页 id / 布局，口径改为 live） */
export const buildLinkedHomeChartUpdateParams = (homeChart = {}, statisticsSnapshot = {}) => ({
	...buildChartUpdateParamsFromSnapshot(homeChart.id, statisticsSnapshot),
	refreshMode: 'live',
	usesPageFilters: false,
});

/** 首页保存后，写回图表库源图（保持源图 id、刷新模式与跟页开关） */
export const buildStatisticsChartUpdateParamsFromHomeChart = (statisticsSnapshot = {}, homeChart = {}) => ({
	...buildChartUpdateParamsFromSnapshot(statisticsSnapshot.id, homeChart),
	refreshMode: statisticsSnapshot.refreshMode === 'live' ? 'live' : 'frozen',
	usesPageFilters: Boolean(statisticsSnapshot.usesPageFilters),
});

export const syncLinkedHomeChartsFromStatisticsSnapshot = async ({ statisticsSnapshot, homeCharts = [], updateHomeChart } = {}) => {
	if (!statisticsSnapshot?.id || typeof updateHomeChart !== 'function') return { updated: 0 };
	const linked = homeCharts.filter((item) => item?.sourceStatisticsChartId === statisticsSnapshot.id);
	let updated = 0;
	for (const homeChart of linked) {
		if (homeChartDefinitionMatchesStatisticsSnapshot(homeChart, statisticsSnapshot)) continue;
		const result = await updateHomeChart(buildLinkedHomeChartUpdateParams(homeChart, statisticsSnapshot));
		if (result?.updated) updated += 1;
	}
	return { updated };
};

export const isStatisticsChartPinnedToHome = (statisticsSnapshot = {}, homeCharts = []) => Boolean(findHomeChartMatchingStatisticsSnapshot(statisticsSnapshot, homeCharts));

export const resolveTrendPeriodSummary = (filters = {}) => {
	const monthlySpec = resolveTrendMonthlySpecForQuery(filters);
	if (monthlySpec?.years?.length) {
		return monthlySpec.years.map((year) => `${year}年`).join(' vs ');
	}

	const periodLabels = [];
	const anchor = filters.trendAnchor;
	if (anchor?.startDate && anchor?.endDate) {
		const anchorLabel = anchor.label?.trim() || resolveComparePeriodChipLabel(anchor);
		if (anchorLabel) periodLabels.push(anchorLabel);
	}

	for (const period of filters.customComparePeriods ?? []) {
		const label = period?.label?.trim() || resolveComparePeriodChipLabel(period);
		if (label) periodLabels.push(label);
	}

	return periodLabels.join(' vs ');
};

export const buildPersistedChartTitle = (filters = {}) => {
	const axisMode = filters.trendAxisMode ?? TREND_AXIS_MODE.TIME;
	const yMetrics = normalizeTrendYAxisMetrics(filters);
	const xLabel = TREND_AXIS_MODE_OPTIONS.find((item) => item.value === axisMode)?.label ?? '时间';
	const yLabel = formatTrendYAxisMetricsLabel(yMetrics, '&');
	const comparePart = resolveTrendPeriodSummary(filters);

	const parts = [xLabel, yLabel, comparePart].filter(Boolean);
	return parts.join(' · ') || '对比分析';
};

export const buildPersistedChartSnapshot = ({ filters = {}, selectedPhase = null, title = '' }) => ({
	id: createSnapshotId(),
	chartKind: PERSISTED_CHART_KIND.TREND,
	title: title?.trim() || buildPersistedChartTitle(filters),
	createdAt: new Date().toISOString(),
	selectedPhase,
	scope: pickStatisticsScopeFields(filters),
	trend: pickTrendFields(filters),
	ui: { showPointLabels: false },
});

/** 经营看板 pipeline-* 图保存/固定首页用快照（保留 chartKind，不洗成 trend） */
export const buildPipelineChartSnapshot = ({ filters = {}, selectedPhase = null, title = '', chartKind } = {}) => ({
	id: createSnapshotId(),
	chartKind: isPipelineBuiltinChartKind(chartKind) ? chartKind : PERSISTED_CHART_KIND.PIPELINE_BAR,
	title: title?.trim() || '流程分布',
	createdAt: new Date().toISOString(),
	selectedPhase,
	scope: pickStatisticsScopeFields(filters),
	trend: pickTrendFields(filters),
	ui: {},
});

export const loadPersistedCharts = (userId, prefix = STATISTICS_PERSISTED_CHARTS_STORAGE_PREFIX) => {
	if (typeof window === 'undefined') return [];
	try {
		const raw = window.localStorage.getItem(getStatisticsPersistedChartsStorageKey(userId, prefix));
		if (!raw) return [];
		const parsed = JSON.parse(raw);
		const max = prefix === HOME_DASHBOARD_CHARTS_STORAGE_PREFIX ? HOME_DASHBOARD_CHARTS_MAX : STATISTICS_PERSISTED_CHARTS_MAX;
		return Array.isArray(parsed) ? trimPersistedChartsToMax(parsed, max) : [];
	} catch {
		return [];
	}
};

/** 服务端持久化上线后，一次性迁移完成后清除本机旧数据 */
export const clearPersistedChartsLocal = (userId, prefix = STATISTICS_PERSISTED_CHARTS_STORAGE_PREFIX) => {
	if (typeof window === 'undefined') return;
	try {
		window.localStorage.removeItem(getStatisticsPersistedChartsStorageKey(userId, prefix));
	} catch {
		// ignore
	}
};

export const appendPersistedChart = (charts = [], snapshot, maxCharts = STATISTICS_PERSISTED_CHARTS_MAX, { dedupe = false } = {}) => {
	if (!snapshot) {
		return { charts, added: false, reason: 'invalid' };
	}
	if (hasReachedPersistedChartsLimit(charts.length, maxCharts)) {
		return { charts, added: false, reason: 'limit' };
	}
	if (dedupe) {
		const sourceId = snapshot.sourceStatisticsChartId;
		if (sourceId && charts.some((item) => item.sourceStatisticsChartId === sourceId)) {
			return { charts, added: false, reason: 'duplicate' };
		}
		const definitionKey = buildPersistedChartDefinitionKey(snapshot);
		if (charts.some((item) => buildPersistedChartDefinitionKey(item) === definitionKey)) {
			return { charts, added: false, reason: 'duplicate' };
		}
	}
	const next = trimPersistedChartsToMax([snapshot, ...charts], maxCharts);
	return { charts: next, added: true, reason: null };
};

export const replacePersistedChart = (charts = [], chartId, nextSnapshot) => {
	if (!chartId || !nextSnapshot) {
		return { charts, updated: false, reason: 'invalid' };
	}
	const index = charts.findIndex((item) => item.id === chartId);
	if (index < 0) {
		return { charts, updated: false, reason: 'missing' };
	}
	const next = charts.map((item) => (item.id === chartId ? nextSnapshot : item));
	return { charts: next, updated: true, reason: null };
};

export const removePersistedChart = (charts = [], chartId) => charts.filter((item) => item.id !== chartId);

export const reorderPersistedCharts = (charts = [], sourceId, targetId) => {
	if (!sourceId || !targetId || sourceId === targetId) return charts;

	const clearLayout = (list) => list.map(clearPersistedChartDefaultGridPlacement);

	if (targetId === DASHBOARD_DRAG_APPEND_TARGET_ID) {
		const fromIndex = charts.findIndex((item) => item.id === sourceId);
		if (fromIndex < 0 || fromIndex >= charts.length - 1) return charts;
		const next = [...charts];
		const [moved] = next.splice(fromIndex, 1);
		next.push(moved);
		return clearLayout(next);
	}

	const fromIndex = charts.findIndex((item) => item.id === sourceId);
	const toIndex = charts.findIndex((item) => item.id === targetId);
	if (fromIndex < 0 || toIndex < 0) return charts;

	const next = [...charts];
	const [moved] = next.splice(fromIndex, 1);
	const insertIndex = fromIndex < toIndex ? toIndex - 1 : toIndex;
	next.splice(insertIndex, 0, moved);
	return clearLayout(next);
};

export const renamePersistedChart = (charts = [], chartId, nextTitle = '') => {
	const title = nextTitle.trim();
	if (!title) return { charts, renamed: false, reason: 'empty' };

	const index = charts.findIndex((item) => item.id === chartId);
	if (index < 0) return { charts, renamed: false, reason: 'missing' };

	const next = charts.map((item) => (item.id === chartId ? { ...item, title } : item));
	return { charts: next, renamed: true, reason: null };
};

export const duplicatePersistedChart = (charts = [], chartId, maxCharts = STATISTICS_PERSISTED_CHARTS_MAX, { appliedFilters = null } = {}) => {
	if (hasReachedPersistedChartsLimit(charts.length, maxCharts)) {
		return { charts, added: false, reason: 'limit', duplicate: null };
	}

	const index = charts.findIndex((item) => item.id === chartId);
	if (index < 0) return { charts, added: false, reason: 'missing', duplicate: null };

	const source = charts[index];
	const cloned = clonePersistedChartSnapshot(source);
	delete cloned.sourceStatisticsChartId;
	delete cloned.source;
	delete cloned.templateId;

	let duplicate = {
		...cloned,
		id: createSnapshotId(),
		title: buildDuplicatePersistedChartTitle(source.title),
		createdAt: new Date().toISOString(),
		usesPageFilters: false,
	};

	if (appliedFilters && typeof appliedFilters === 'object') {
		duplicate = {
			...duplicate,
			scope: pickStatisticsScopeFields(appliedFilters),
			trend: pickTrendFields(appliedFilters),
			usesPageFilters: false,
		};
	}

	const next = [...charts];
	next.splice(index + 1, 0, duplicate);
	return { charts: trimPersistedChartsToMax(next, maxCharts), added: true, reason: null, duplicate };
};

export const updatePersistedChartColSpan = (charts = [], chartId, colSpan, maxColumns = PERSISTED_CHART_COL_SPAN_MAX) => {
	const index = charts.findIndex((item) => item.id === chartId);
	if (index < 0) return { charts, updated: false, reason: 'missing', colSpan: null };

	const nextSpan = normalizePersistedChartColSpan(colSpan, maxColumns);
	const storedRaw = charts[index]?.ui?.colSpan;
	const hasStored = Number.isFinite(storedRaw) && storedRaw >= PERSISTED_CHART_COL_SPAN_MIN && storedRaw <= PERSISTED_CHART_COL_SPAN_MAX;
	const currentStored = hasStored ? normalizePersistedChartColSpan(storedRaw, maxColumns) : null;
	if (currentStored === nextSpan) {
		return { charts, updated: false, reason: 'unchanged', colSpan: currentStored ?? nextSpan };
	}

	const next = charts.map((item) =>
		item.id === chartId
			? {
					...clearPersistedChartDefaultGridPlacement(item),
					ui: {
						...(item.ui ?? {}),
						colSpan: nextSpan,
						colSpanTouched: true,
						useDefaultGridPlacement: undefined,
					},
				}
			: item,
	);
	return { charts: next, updated: true, reason: null, colSpan: nextSpan };
};

export const cyclePersistedChartColSpan = (charts = [], chartId, maxColumns = PERSISTED_CHART_COL_SPAN_MAX) => {
	const index = charts.findIndex((item) => item.id === chartId);
	if (index < 0) return { charts, updated: false, reason: 'missing', colSpan: null };

	const currentSpan = resolvePersistedChartColSpan(charts[index], index, charts.length, maxColumns);
	const nextSpan = getPersistedChartColSpanCycle(currentSpan, maxColumns);
	return updatePersistedChartColSpan(charts, chartId, nextSpan, maxColumns);
};

export const updatePersistedChartRowSpan = (charts = [], chartId, rowSpan) => {
	const index = charts.findIndex((item) => item.id === chartId);
	if (index < 0) return { charts, updated: false, reason: 'missing', rowSpan: null };

	const nextSpan = normalizePersistedChartRowSpan(rowSpan);
	const storedRaw = charts[index]?.ui?.rowSpan;
	const hasStored = Number.isFinite(storedRaw) && storedRaw >= PERSISTED_CHART_ROW_SPAN_MIN && storedRaw <= PERSISTED_CHART_ROW_SPAN_MAX;
	const currentStored = hasStored ? normalizePersistedChartRowSpan(storedRaw) : null;
	if (currentStored === nextSpan) {
		return { charts, updated: false, reason: 'unchanged', rowSpan: currentStored ?? nextSpan };
	}

	const next = charts.map((item) =>
		item.id === chartId
			? {
					...clearPersistedChartDefaultGridPlacement(item),
					ui: {
						...(item.ui ?? {}),
						rowSpan: nextSpan,
						rowSpanTouched: true,
						useDefaultGridPlacement: undefined,
					},
				}
			: item,
	);
	return { charts: next, updated: true, reason: null, rowSpan: nextSpan };
};

export const cyclePersistedChartRowSpan = (charts = [], chartId) => {
	const index = charts.findIndex((item) => item.id === chartId);
	if (index < 0) return { charts, updated: false, reason: 'missing', rowSpan: null };

	const currentSpan = resolvePersistedChartRowSpan(charts[index]);
	const nextSpan = getPersistedChartRowSpanCycle(currentSpan);
	return updatePersistedChartRowSpan(charts, chartId, nextSpan);
};
